import {
    Box,
    Heading,
    HStack,
    Separator,
    type StackProps,
    VStack,
} from '@chakra-ui/react';
import React from 'react';

// The standard full-content error screen (logo | status code + title, an
// optional mono message box, and an Actions row). Purely presentational —
// error parsing (trpc/router/status extraction) is app business; apps wrap
// this with their own logic (e.g. enterprise's ErrorBox) or render it
// directly for known states.
export type ErrorScreenProps = StackProps & {
    logo: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    statusCode: number;
    title: string;
    message?: string;
};

const ErrorScreenRoot = React.forwardRef<HTMLDivElement, ErrorScreenProps>(
    ({ children, logo: Logo, statusCode, title, message, ...rest }, ref) => {
        return (
            <VStack ref={ref} gap={8} p={{ base: 6, md: 32 }} {...rest}>
                <HStack gap={5}>
                    <Logo style={{ width: '80px', height: '80px' }} />
                    <Separator
                        orientation='vertical'
                        height={28}
                        borderColor='border.muted'
                    />
                    <Box>
                        {/* Error screens render no page heading, so the
                            status code is the page's h1. */}
                        <Heading as='h1' size='5xl'>
                            {statusCode}
                        </Heading>
                        <Heading
                            size='2xl'
                            fontWeight='normal'
                            color='fg.muted'
                        >
                            {title}
                        </Heading>
                    </Box>
                </HStack>
                {message && (
                    <Box
                        p={4}
                        bg='bg.subtle'
                        border='1px'
                        borderStyle='solid'
                        borderColor='border'
                        borderRadius='xl'
                        fontFamily='mono'
                        fontSize='sm'
                        overflowX='auto'
                    >
                        {message}
                    </Box>
                )}
                {children}
            </VStack>
        );
    },
);

ErrorScreenRoot.displayName = 'ErrorScreen';

type ErrorScreenActionsProps = StackProps & {
    children?: React.ReactNode;
};

function ErrorScreenActions({ children, ...props }: ErrorScreenActionsProps) {
    return (
        <HStack justifyContent='space-between' {...props}>
            {children}
        </HStack>
    );
}

type ErrorScreenNamespace = typeof ErrorScreenRoot & {
    Actions: typeof ErrorScreenActions;
};

export const ErrorScreen = Object.assign(ErrorScreenRoot, {
    Actions: ErrorScreenActions,
}) as ErrorScreenNamespace;
